import { useState, useEffect, useCallback } from 'react'
import apiClient from '../utils/api'

export const useReports = () => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [data, setData] = useState({
        summary: null,
        detailed: null,
        chart: null,
        filters: null
    })
    const [savedFilters, setSavedFilters] = useState([])

    const fetchReports = useCallback(async (params = {}) => {
        setLoading(true)
        setError(null)
        
        try {
            const queryParams = new URLSearchParams()
            
            // Add filters - handle arrays for all filters
            if (params.team && params.team.length > 0) {
                if (Array.isArray(params.team)) {
                    params.team.forEach(teamId => {
                        if (teamId) queryParams.append('team[]', teamId)
                    })
                } else {
                    queryParams.append('team[]', params.team)
                }
            }
            
            if (params.clients && params.clients.length > 0) {
                if (Array.isArray(params.clients)) {
                    params.clients.forEach(clientId => {
                        if (clientId) queryParams.append('clients[]', clientId)
                    })
                } else {
                    queryParams.append('clients[]', params.clients)
                }
            }
            
            if (params.projects && params.projects.length > 0) {
                if (Array.isArray(params.projects)) {
                    params.projects.forEach(projectId => {
                        if (projectId) queryParams.append('projects[]', projectId)
                    })
                } else {
                    queryParams.append('projects[]', params.projects)
                }
            }
            
            if (params.tasks && params.tasks.length > 0) {
                if (Array.isArray(params.tasks)) {
                    params.tasks.forEach(taskId => {
                        if (taskId) queryParams.append('tasks[]', taskId)
                    })
                } else {
                    queryParams.append('tasks[]', params.tasks)
                }
            }
            
            // Add date range
            if (params.start_date) queryParams.set('start_date', params.start_date)
            if (params.end_date) queryParams.set('end_date', params.end_date)

            // Add pagination for detailed view
            if (params.page) queryParams.set('page', params.page)
            if (params.per_page) queryParams.set('per_page', params.per_page)

            // Add rounding option
            if (params.enable_rounding !== undefined) queryParams.set('enable_rounding', params.enable_rounding)

            // Add show billable column option
            if (params.show_billable_column !== undefined) queryParams.set('show_billable_column', params.show_billable_column)

            const endpoint = queryParams.toString() ? `/reports?${queryParams}` : '/reports'
            const result = await apiClient.get(endpoint)
            setData(result)
        } catch (err) {
            setError(err.message)
            console.error('Failed to fetch reports:', err)
        } finally {
            setLoading(false)
        }
    }, [])

    const fetchFilterOptions = useCallback(async () => {
        try {
            const result = await apiClient.get('/reports/filter_options')
            setData(prev => ({ ...prev, filters: result }))
            return result
        } catch (err) {
            setError(err.message)
            console.error('Failed to fetch filter options:', err)
            return null
        }
    }, [])

    const exportReports = useCallback(async (format, params = {}) => {
        setLoading(true)
        setError(null)
        
        try {
            const queryParams = new URLSearchParams()
            
            // Add filters
            if (params.team) queryParams.set('team', params.team)
            if (params.clients) queryParams.set('clients', params.clients)
            if (params.projects) queryParams.set('projects', params.projects)
            if (params.tasks) queryParams.set('tasks', params.tasks)
            
            // Add date range
            if (params.start_date) queryParams.set('start_date', params.start_date)
            if (params.end_date) queryParams.set('end_date', params.end_date)
            
            // Add format
            queryParams.set('format', format.toLowerCase())
            
            // Add report type (detailed or summary)
            if (params.report_type) queryParams.set('report_type', params.report_type.toLowerCase())

            // Add rounding option
            if (params.enable_rounding !== undefined) queryParams.set('enable_rounding', params.enable_rounding)

            const response = await fetch('/api/v1/reports/export', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    format: format.toLowerCase(),
                    report_type: params.report_type?.toLowerCase() || 'detailed',
                    group_by: params.group_by || [],
                    filters: {
                        team: Array.isArray(params.team) ? params.team : (params.team ? [params.team] : []),
                        clients: Array.isArray(params.clients) ? params.clients : (params.clients ? [params.clients] : []),
                        projects: Array.isArray(params.projects) ? params.projects : (params.projects ? [params.projects] : []),
                        tasks: Array.isArray(params.tasks) ? params.tasks : (params.tasks ? [params.tasks] : []),
                        show_billable_column: params.show_billable_column || false
                    },
                    date_range: {
                        start_date: params.start_date,
                        end_date: params.end_date
                    }
                })
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            // Handle all file downloads as blobs
            const blob = await response.blob()
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url

            // Try to get filename from Content-Disposition header
            const contentDisposition = response.headers.get('Content-Disposition')
            let fileName = null

            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
                if (fileNameMatch && fileNameMatch[1]) {
                    fileName = fileNameMatch[1].replace(/['"]/g, '')
                }
            }

            // Fallback to default filename if not found in header
            if (!fileName) {
                const reportType = params.report_type?.toLowerCase() || 'detailed'
                let fileExtension = format.toLowerCase()

                switch (format.toLowerCase()) {
                    case 'pdf':
                        fileExtension = 'pdf'
                        break
                    case 'xls':
                    case 'xlsx':
                    case 'excel':
                        fileExtension = 'xls'
                        break
                    case 'csv':
                        fileExtension = 'csv'
                        break
                    default:
                        fileExtension = format.toLowerCase()
                }

                fileName = `kadle-${reportType}-report-${params.start_date}-to-${params.end_date}.${fileExtension}`
            }

            a.download = fileName
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
        } catch (err) {
            setError(err.message)
            console.error('Failed to export reports:', err)
        } finally {
            setLoading(false)
        }
    }, [])

    const fetchSavedFilters = useCallback(async () => {
        try {
            const result = await apiClient.get('/saved_filters')
            setSavedFilters(result)
            return result
        } catch (err) {
            setError(err.message)
            console.error('Failed to fetch saved filters:', err)
            return []
        }
    }, [])

    const saveSavedFilter = useCallback(async (name, filters, groupByFields = [], additionalOptions = {}) => {
        setLoading(true)
        setError(null)

        try {
            // Merge additional options (showBillableColumn, enableRounding, dateRange) into filters
            const enrichedFilters = {
                ...filters,
                show_billable_column: additionalOptions.showBillableColumn,
                enable_rounding: additionalOptions.enableRounding,
                date_range_preset: additionalOptions.dateRangePreset,
                start_date: additionalOptions.startDate,
                end_date: additionalOptions.endDate
            }

            const result = await apiClient.post('/saved_filters', {
                saved_filter: {
                    name: name,
                    filters: enrichedFilters,
                    group_by_fields: groupByFields
                }
            })

            // Refresh saved filters list
            await fetchSavedFilters()
            return result
        } catch (err) {
            setError(err.message)
            console.error('Failed to save filter:', err)
            throw err
        } finally {
            setLoading(false)
        }
    }, [fetchSavedFilters])

    const deleteSavedFilter = useCallback(async (filterId) => {
        setLoading(true)
        setError(null)
        
        try {
            await apiClient.delete(`/saved_filters/${filterId}`)
            // Refresh saved filters list
            await fetchSavedFilters()
        } catch (err) {
            setError(err.message)
            console.error('Failed to delete filter:', err)
            throw err
        } finally {
            setLoading(false)
        }
    }, [fetchSavedFilters])

    const fetchGroupedReports = useCallback(async (params = {}, groupByFields = []) => {
        try {
            const queryParams = new URLSearchParams()
            
            // Add filters - handle arrays for all filters
            if (params.team && params.team.length > 0) {
                if (Array.isArray(params.team)) {
                    params.team.forEach(teamId => {
                        if (teamId) queryParams.append('team[]', teamId)
                    })
                } else {
                    queryParams.append('team[]', params.team)
                }
            }
            
            if (params.clients && params.clients.length > 0) {
                if (Array.isArray(params.clients)) {
                    params.clients.forEach(clientId => {
                        if (clientId) queryParams.append('clients[]', clientId)
                    })
                } else {
                    queryParams.append('clients[]', params.clients)
                }
            }
            
            if (params.projects && params.projects.length > 0) {
                if (Array.isArray(params.projects)) {
                    params.projects.forEach(projectId => {
                        if (projectId) queryParams.append('projects[]', projectId)
                    })
                } else {
                    queryParams.append('projects[]', params.projects)
                }
            }
            
            if (params.tasks && params.tasks.length > 0) {
                if (Array.isArray(params.tasks)) {
                    params.tasks.forEach(taskId => {
                        if (taskId) queryParams.append('tasks[]', taskId)
                    })
                } else {
                    queryParams.append('tasks[]', params.tasks)
                }
            }
            
            // Add date range
            if (params.start_date) queryParams.set('start_date', params.start_date)
            if (params.end_date) queryParams.set('end_date', params.end_date)
            
            // Add group by fields
            if (groupByFields && groupByFields.length > 0) {
                groupByFields.forEach(field => {
                    queryParams.append('group_by[]', field)
                })
            }

            const endpoint = queryParams.toString() ? `/reports/grouped?${queryParams}` : '/reports/grouped'
            const result = await apiClient.get(endpoint)
            return result
        } catch (err) {
            console.error('Failed to fetch grouped reports:', err)
            throw err
        }
    }, [])

    return {
        data,
        loading,
        error,
        savedFilters,
        fetchReports,
        fetchFilterOptions,
        exportReports,
        fetchSavedFilters,
        saveSavedFilter,
        deleteSavedFilter,
        fetchGroupedReports
    }
}

export default useReports